// src/components/TenantAgreementModalView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font, PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { generateAgreementPDF, getAgreementJsonData, downloadAgreement } from '../apis/services/pgApi';

// ─────────────────────────────────────────────────────────────────
// LETTERHEAD ARTWORK
// ─────────────────────────────────────────────────────────────────
const LETTERHEAD_FIRST = '/assets/letterhead-first.png';

// ─────────────────────────────────────────────────────────────────
// FONT REGISTRATION
// ─────────────────────────────────────────────────────────────────
Font.register({
  family: 'NotoSans',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf', fontWeight: 'normal' },
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Bold.ttf', fontWeight: 'bold' },
  ],
});

// ═════════════════════════════════════════════════════════════════
// PDF STYLES
// ═════════════════════════════════════════════════════════════════

const PAGE_PADDING_TOP = 120;
const PAGE_PADDING_BOTTOM = 60;
const PAGE_PADDING_SIDE = 30;

const HEADER_NAME_TOP = 38;
const HEADER_NAME_LEFT = 30;
const HEADER_NAME_WIDTH = 240;
const HEADER_ROW_LEFT = 340;
const HEADER_ROW_WIDTH = 210;
const HEADER_ROW1_TOP = 46;
const HEADER_ROW2_TOP = 62;
const HEADER_ROW3_TOP = 79;
const FOOTER_TEXT_BOTTOM = 40;

const docStyles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PADDING_TOP,
    paddingBottom: PAGE_PADDING_BOTTOM,
    paddingHorizontal: PAGE_PADDING_SIDE,
    fontSize: 9.5,
    lineHeight: 1.45,
    fontFamily: 'Helvetica',
    color: '#1F2937',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 1,
  },
  sectionHeading: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    marginTop: 10,
    marginBottom: 4,
  },
  bold: { fontFamily: 'Helvetica-Bold' },
  keyword: { fontFamily: 'Helvetica-Bold' },
  rupee: { fontFamily: 'NotoSans' },
  clause: { marginBottom: 4, textAlign: 'left' },
  signBlockWrap: { marginTop: 18 },
  signRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 22 },
  signCol: { width: '46%' },
  signLabel: { fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  signLine: { borderBottomWidth: 1, borderBottomColor: '#374151', marginBottom: 3 },
  signSub: { fontSize: 8.5, color: '#4B5563' },
  pgName: {
    position: 'absolute',
    top: HEADER_NAME_TOP,
    left: HEADER_NAME_LEFT,
    width: HEADER_NAME_WIDTH,
    fontSize: 24,
    lineHeight: 1.2,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
  pgContactRow: {
    position: 'absolute',
    left: HEADER_ROW_LEFT,
    width: HEADER_ROW_WIDTH,
    fontSize: 8,
    lineHeight: 1.25,
    color: '#374151',
  },
  pgFooter: {
    position: 'absolute',
    bottom: FOOTER_TEXT_BOTTOM,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
});

// ═════════════════════════════════════════════════════════════════
// SIMPLIFIED FORM STYLES (for the single-page admission form)
// ═════════════════════════════════════════════════════════════════
const simpleStyles = StyleSheet.create({
  twoColRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  colBox: { width: '48%' },
  infoBox: {
    borderWidth: 0,
    borderColor: '#9CA3AF',
    padding: 8,
  },
  infoBoxTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
    textDecoration: 'underline',
    marginBottom: 6,
  },
  officeRow: { flexDirection: 'row', marginBottom: 3 },
  officeLabel: { fontFamily: 'Helvetica-Bold', width: 100 },
});

// Renders text containing **bold** markers as mixed bold/normal Text spans
const RUPEE = '₹';

// Splits a plain-text run on the rupee sign and renders that glyph in NotoSans,
// since Helvetica (react-pdf's default base font) has no ₹ glyph and mis-renders it.
const renderWithRupee = (text, keyPrefix, bold) => {
  const segments = String(text).split(RUPEE);
  const nodes = [];
  segments.forEach((seg, idx) => {
    if (seg) nodes.push(<Text key={`${keyPrefix}-t${idx}`}>{seg}</Text>);
    if (idx < segments.length - 1) {
      nodes.push(
        <Text key={`${keyPrefix}-r${idx}`} style={[docStyles.rupee, bold && { fontWeight: 'bold' }]}>
          {RUPEE}
        </Text>
      );
    }
  });
  return nodes;
};

const parseBoldText = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <Text key={i} style={docStyles.bold}>{renderWithRupee(part.slice(2, -2), `b${i}`, true)}</Text>
      : <Text key={i}>{renderWithRupee(part, `n${i}`, false)}</Text>
  );
};

const NumberedClause = ({ number, text }) => (
  <Text style={docStyles.clause}>{number}. {parseBoldText(text)}</Text>
);

// Static Terms & Conditions — verbatim from the signed hostel agreement, unchanged
const STATIC_TERMS = [
  'Rent is not negotiable post the signing of rent agreement and acceptance of terms & conditions.',
  'Booking amount (or token money provided) will be **non-refundable on cancellation**.',
  'Electricity bill of the room and washroom is **excluded from the rent** and should be paid as per the rate **Unit 14.5 Rs. + GST**. The bill to be charged is divisible by number of occupants in the room. In the absence of other occupants, the bill shall be paid wholly by the only occupant.',
  'Due date of rent payment will be **the 5th of every month**. The delay in payment will attract **a penalty of Rs. 100 per day**.',
  'PG entry and exit **timings are 7:00 AM and 11 PM respectively.** In the event of delayed arrival or early departure to/from the PG premises, the occupant must inform the Warden/Caretaker/Guard for such arrival/departure in advance.',
  "Damage to any immovable/movable property of the premises will attract a fine of **up to Rs. 1,000** and it must also compensate for the losses/damages to the PG's property. The figure for losses/damages will be charged **at market value** of the objects/property so damaged and not at any depreciable value.",
  'Since we provide a budget laundry service at just ₹300/month (not luxury/dry cleaning), our maximum liability for any lost or damaged item is **strictly capped at upto ₹300**. If you prefer premium care, you can opt out and we will deduct ₹300 from your rent.',
  'Any misbehave with the staff will be not acceptable and strict action will be taken.',
  'Lock in period in from **1st August to 20th June**.',
  'The official room **tenure is from 1st August till 20th June**. For students moving in during July, rent will be charged on a daily pro-rata basis.',
  'Security is **non refundable during the 11 month Tenure** only refund at the end of Tenure.',
  'Strictly **No Smoking & No Drinking** within the premises.',
  'Outsiders / visitors are not allowed inside the room.',
  'Student **cannot vacate the seat before lock in period duration**. If vacate the seat, security will be forfeited.',
];

const Section = ({ children }) => <Text style={docStyles.sectionHeading}>{children}</Text>;

// ─── DATA TRANSFORMATION FUNCTION ──────────────────────────────
const transformAgreementData = (apiData) => {
  if (!apiData) return null;

  // If data is already in the expected format
  if (apiData.pg && !Array.isArray(apiData.pg) && apiData.tenant && !Array.isArray(apiData.tenant)) {
    return apiData;
  }

  // Handle array data from API
  const getFirstItem = (data) => {
    if (Array.isArray(data) && data.length > 0) {
      return data[0];
    }
    return data || {};
  };

  return {
    agreementDate: apiData.agreementDate || '20-07-2026',
    agreementDay: apiData.agreementDay || 'Monday',
    city: apiData.city || 'Delhi',
    state: apiData.state || 'Delhi',
    
    pg: getFirstItem(apiData.pg),
    landlord: getFirstItem(apiData.landlord),
    tenant: getFirstItem(apiData.tenant),
    property: getFirstItem(apiData.property),
    tenancy: getFirstItem(apiData.tenancy),
    rent: getFirstItem(apiData.rent),
    deposit: getFirstItem(apiData.deposit),
    utilities: getFirstItem(apiData.utilities),
    food: getFirstItem(apiData.food),
    rules: getFirstItem(apiData.rules),
    termination: getFirstItem(apiData.termination)
  };
};

// ═════════════════════════════════════════════════════════════════
// SIMPLIFIED SINGLE-PAGE ADMISSION FORM
// ═════════════════════════════════════════════════════════════════

export const RentAgreementDocument = ({ data, isVerified = false } = {}) => {
  const rawData = data;
  const d = transformAgreementData(rawData);

  const pg = d.pg || {};
  const lo = d.landlord || {};
  const tn = d.tenant || {};
  const pr = d.property || {};
  const tc = d.tenancy || {};

  return (
    <Document>
      <Page size="A4" style={docStyles.page} wrap>
        <Image
          src={LETTERHEAD_FIRST}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 595.28,
            height: 841.89,
            zIndex: -1,
          }}
          fixed
        />

        <Text style={docStyles.pgName} fixed>{pg.name || 'AapkaPG'}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW1_TOP }]} fixed>{pg.phone || ''}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW2_TOP }]} fixed>{pg.email || ''}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW3_TOP }]} fixed>{pg.address || ''}</Text>
        <Text style={docStyles.pgFooter} fixed>Thank you for choosing {pg.name || 'AapkaPG'}</Text>

        <Text style={docStyles.title}>RESIDENT ADMISSION FORM & HOSTEL AGREEMENT</Text>

        {/* ── FOR OFFICE USE ONLY  +  PERSONAL DETAILS (side by side) ── */}
        <View style={simpleStyles.twoColRow}>
          <View style={simpleStyles.colBox}>
            <View style={simpleStyles.infoBox}>
              <Text style={simpleStyles.infoBoxTitle}>For Office Use Only</Text>
              <View style={simpleStyles.officeRow}>
                <Text style={simpleStyles.officeLabel}>Room No:</Text>
                <Text style={docStyles.keyword}>{pr.RoomNmber || ''}</Text>
              </View>
              <View style={simpleStyles.officeRow}>
                <Text style={simpleStyles.officeLabel}>Joining Date:</Text>
                <Text style={docStyles.keyword}>{tc.startDate || ''}</Text>
              </View>
            </View>
          </View>

          <View style={simpleStyles.colBox}>
            <View style={simpleStyles.infoBox}>
              <Text style={simpleStyles.infoBoxTitle}>Personal Details</Text>
              <Text style={docStyles.clause}>Student Full Name: <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text></Text>
              <Text style={docStyles.clause}>Contact No (Self): <Text style={docStyles.keyword}>{tn.mobile || ''}</Text></Text>
              {tn.parent ? (
                <Text style={docStyles.clause}>Father's Name: <Text style={docStyles.keyword}>{tn.parent}</Text></Text>
              ) : null}
              <Text style={docStyles.clause}>
                Permanent Address: <Text style={docStyles.keyword}>{tn.address || ''}</Text>
              </Text>
              <Text style={docStyles.clause}>
                {tn.pin ? <Text>PIN: <Text style={docStyles.keyword}>{tn.pin}</Text></Text> : null}
              </Text>
              {tn.email ? (
                <Text style={docStyles.clause}>Email ID: <Text style={docStyles.keyword}>{tn.email}</Text></Text>
              ) : null}
            </View>
          </View>
        </View>

        {/* ── TERMS & CONDITIONS (static, unchanged) ──────────── */}
        <Section>Terms & Conditions</Section>
        {STATIC_TERMS.map((t, i) => (
          <NumberedClause key={i} number={i + 1} text={t} />
        ))}

        {/* ── SIGNATURES (unchanged from the full agreement) ──── */}
        <View style={docStyles.signBlockWrap}>
          <View style={docStyles.signRow}>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Landlord</Text>
              <Text style={docStyles.signSub}>Signature: 
                {isVerified ? (
                  <Text> {lo.name || ''}</Text>
                ) : (
                  <Text> </Text>
                )}</Text>
              <View style={docStyles.signLine} />
              {/* <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{lo.title || 'Mr.'} {lo.name || ''}</Text></Text> */}
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Tenant</Text>
              <Text style={docStyles.signSub}>Signature:
                {isVerified ? (
                  <Text> {tn.name || ''}</Text>
                ) : (
                  <Text> </Text>
                )}
              </Text>
              <View style={docStyles.signLine} />
              {/* <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text></Text> */}
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// ═════════════════════════════════════════════════════════════════
// TENANT AGREEMENT MODAL VIEW - Main Component
// ═════════════════════════════════════════════════════════════════

const TenantAgreementModalView = ({ 
  isOpen, 
  onClose, 
  pgId, 
  tenantId,
  agreementStatus = 'DRAFT',
  statusText = 'Draft',
  statusColor = '#F59E0B',
  showSaveToCloud = false,
  onUploadSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isManualUploading, setIsManualUploading] = useState(false);
  const [manualUploadStatus, setManualUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Check if mobile
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
  }, []);

  const handleOpenPDF = async () => {
    const { pdf } = await import('@react-pdf/renderer');
    const blob = await pdf(<RentAgreementDocument data={agData} isVerified={isVerified} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
    
  // Fetch agreement data when modal opens
  useEffect(() => {
    if (isOpen && pgId && tenantId) {
      fetchAgreementData();
    }
  }, [isOpen, pgId, tenantId]);

  const fetchAgreementData = async () => {
    if (!pgId || !tenantId) {
      setError('PG ID and Tenant ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getAgreementJsonData(pgId, tenantId);
      console.log('Agreement API Response:', response);

      if (response.success && response.data) {
        let parsedData = response.data;
        
        // Parse agreementjson if it's a string
        if (typeof response.data.agreementjson === 'string') {
          try {
            const jsonData = JSON.parse(response.data.agreementjson);
            if (Array.isArray(jsonData) && jsonData.length > 0) {
              parsedData = {
                ...response.data,
                agreementjson: jsonData[0]
              };
            } else {
              parsedData = {
                ...response.data,
                agreementjson: jsonData
              };
            }
          } catch (parseError) {
            console.error('Error parsing agreement JSON:', parseError);
            parsedData = response.data;
          }
        } else if (Array.isArray(response.data.agreementjson)) {
          parsedData = {
            ...response.data,
            agreementjson: response.data.agreementjson[0] || response.data.agreementjson
          };
        }

        setAgreementData(parsedData);
        
        // Check if file exists in cloud
        if (parsedData.AgreementID) {
          await fetchDownloadedFile(parsedData.AgreementID);
        }
      } else {
        setError(response.message || 'Failed to fetch agreement data');
      }
    } catch (err) {
      console.error('Error fetching agreement:', err);
      setError(err.message || 'An error occurred while fetching agreement');
    } finally {
      setLoading(false);
    }
  };

  // ─── Fetch Downloaded File Info ──────────────────────────────────
  const fetchDownloadedFile = async (agreementId) => {
    try {
      const response = await downloadAgreement(agreementId);
      if (response.success && response.data && response.data.PDFUrl) {
        setDownloadedFile(response.data);
        return response.data;
      }
      setDownloadedFile(null);
      return null;
    } catch (error) {
      console.error('Error fetching downloaded file:', error);
      setDownloadedFile(null);
      return null;
    }
  };

  // ─── Upload to Cloud (Auto-generate) ──────────────────────────────
  const handleGenerateAndUpload = async () => {
    const agreementId = agreementData?.AgreementID;
    
    if (!agreementId) {
      alert('Agreement ID is required to save the document.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadStatus('generating');
      setUploadError(null);

      // Generate PDF as blob
      const pdfBlob = await pdf(
        <RentAgreementDocument data={agreementData?.agreementjson} isVerified={isVerified} />
      ).toBlob();

      // Create File object
      const file = new File(
        [pdfBlob],
        `agreement_${agreementId}_${Date.now()}.pdf`,
        { type: 'application/pdf' }
      );

      setUploadStatus('uploading');

      // Call API to upload
      const result = await generateAgreementPDF(
        agreementId,
        file
      );

      if (result.success) {
        setUploadStatus('success');
        console.log('✅ PDF uploaded successfully:', result.data);
        
        // Refresh downloaded file info
        await fetchDownloadedFile(agreementId);
        
        if (onUploadSuccess) {
          onUploadSuccess(result.data);
        }
      } else {
        setUploadStatus('error');
        setUploadError(result.message || 'Failed to upload PDF');
      }
    } catch (error) {
      console.error('Error:', error);
      setUploadStatus('error');
      setUploadError(error.message || 'An error occurred while uploading');
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Manual Upload (User selects file) ──────────────────────────
  const handleManualUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const agreementId = agreementData?.AgreementID;
    if (!agreementId) {
      alert('Agreement ID is required to upload.');
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file.');
      event.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size should be less than 10MB.');
      event.target.value = '';
      return;
    }

    try {
      setIsManualUploading(true);
      setManualUploadStatus('uploading');
      setUploadError(null);

      const result = await generateAgreementPDF(agreementId, file);

      if (result.success) {
        setManualUploadStatus('success');
        console.log('✅ PDF uploaded successfully:', result.data);
        
        // Refresh downloaded file info
        await fetchDownloadedFile(agreementId);
        
        if (onUploadSuccess) {
          onUploadSuccess(result.data);
        }
        
        // Reset file input
        event.target.value = '';
        
        // Auto-clear success message after 5 seconds
        setTimeout(() => {
          setManualUploadStatus(null);
        }, 5000);
      } else {
        setManualUploadStatus('error');
        setUploadError(result.message || 'Failed to upload PDF');
        setTimeout(() => {
          setManualUploadStatus(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setManualUploadStatus('error');
      setUploadError(error.message || 'An error occurred while uploading');
      setTimeout(() => {
        setManualUploadStatus(null);
      }, 5000);
    } finally {
      setIsManualUploading(false);
    }
  };

  // ─── Handle Download ─────────────────────────────────────────────
  const handleDownload = async () => {
    const agreementId = agreementData?.AgreementID;
    if (!agreementId) {
      alert('Agreement ID is required to download.');
      return;
    }

    try {
      setIsDownloading(true);
      
      // First check if file exists
      const fileInfo = await fetchDownloadedFile(agreementId);
      
      if (fileInfo && fileInfo.PDFUrl) {
        // Open the PDF in a new tab
        window.open(fileInfo.PDFUrl, '_blank');
      } else {
        alert('No PDF file found for this agreement. Please upload a file first.');
      }
    } catch (error) {
      console.error('Error downloading agreement:', error);
      alert(error.message || 'Failed to download agreement');
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── Close handler ──────────────────────────────────────────────
  const handleClose = () => {
    setError(null);
    setUploadStatus(null);
    setUploadError(null);
    setManualUploadStatus(null);
    onClose();
  };

  // ─── Render ──────────────────────────────────────────────────────
  if (!isOpen) return null;

  const agData = agreementData?.agreementjson || null;
  const tenantName = agData?.tenant?.[0]?.name || agData?.tenant?.name || 'Tenant';
  const roomNumber = agData?.property?.[0]?.RoomNmber || agData?.property?.RoomNmber || 'TBD';
  const agreementId = agreementData?.AgreementID;
  const isVerified = agreementStatus === 'TENANT_ACCEPTED' && agreementData?.WithorWithoutOtp === true;
  const hasDownloadedFile = downloadedFile && downloadedFile.PDFUrl;

  return (
    <div className="va-overlay" onClick={handleClose}>
      <div className="va-modal" onClick={e => e.stopPropagation()}>
        {/* ─── Modal Header ─────────────────────────────────────── */}
        <div className="va-modal-head">
          <div>
            <h3 className="va-modal-title">Rent Agreement</h3>
            <p className="va-modal-sub">
              {tenantName} · Room {roomNumber}
              {agreementStatus && (
                <span style={{ 
                  marginLeft: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: statusColor,
                  padding: '2px 10px',
                  borderRadius: '12px',
                  backgroundColor: `${statusColor}15`,
                  border: `1px solid ${statusColor}30`,
                }}>
                  {statusText}
                </span>
              )}
            </p>
          </div>

          <div className="va-modal-actions">
            {/* ─── NEW: Manual Upload Button ─────────────────────── */}
            {agreementId && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,application/pdf"
                  style={{ display: 'none' }}
                />
                <button 
                  className="va-upload-btn" 
                  onClick={handleManualUploadClick}
                  disabled={isManualUploading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isManualUploading ? '#9CA3AF' : '#7C3AED',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isManualUploading ? 'not-allowed' : 'pointer',
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  {isManualUploading ? (
                    <>
                      <span className="spinner" style={{
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload PDF
                    </>
                  )}
                </button>
                {manualUploadStatus === 'success' && (
                  <span style={{
                    color: '#059669',
                    fontSize: '12px',
                    fontWeight: '500',
                    marginRight: '8px'
                  }}>
                    ✅ Uploaded
                  </span>
                )}
                {manualUploadStatus === 'error' && (
                  <span style={{
                    color: '#DC2626',
                    fontSize: '12px',
                    fontWeight: '500',
                    marginRight: '8px'
                  }}>
                    ❌ Failed
                  </span>
                )}
              </>
            )}

            {/* ─── NEW: Download Button ──────────────────────────── */}
            {hasDownloadedFile && (
              <button 
                className="va-download-btn" 
                onClick={handleDownload}
                disabled={isDownloading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isDownloading ? '#9CA3AF' : '#2563EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                {isDownloading ? (
                  <>
                    <span className="spinner" style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download PDF
                  </>
                )}
              </button>
            )}

            {/* ─── Existing: Save to Cloud Button ────────────────── */}
            {/* {agreementId && showSaveToCloud && (
              <button 
                className="va-upload-btn" 
                onClick={handleGenerateAndUpload}
                disabled={isUploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isUploading ? '#9CA3AF' : '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                {isUploading ? (
                  <>
                    <span className="spinner" style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    {uploadStatus === 'generating' ? 'Generating...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                    </svg>
                    Save to Cloud
                  </>
                )}
              </button>
            )} */}

            {/* ─── Existing: Mobile Open PDF ────────────────────── */}
            {isMobile && ( 
              <button className="va-download-btn" onClick={handleOpenPDF} style={{ marginRight: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                Open PDF
              </button>
            )}
            
            {/* ─── Existing: PDFDownloadLink ────────────────────── */}
            {/* {agData && 
             !hasDownloadedFile && 
            (
              <PDFDownloadLink
                document={<RentAgreementDocument data={agData} isVerified={isVerified} />}
                fileName={`Agreement_${agreementId || 'Rent'}.pdf`}
                className="va-download-btn"
              >
                {({ loading }) => (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    {loading ? 'Preparing…' : 'Download PDF'}
                  </>
                )}
              </PDFDownloadLink>
            )} */}

            <button className="va-modal-close" onClick={handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* ─── Upload Status Messages ──────────────────────────── */}
        {uploadStatus === 'success' && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#D1FAE5',
            color: '#065F46',
            borderBottom: '1px solid #A7F3D0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            PDF uploaded successfully to cloud storage
          </div>
        )}
        {uploadStatus === 'error' && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            borderBottom: '1px solid #FCA5A5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>❌</span>
            {uploadError || 'Failed to upload PDF. Please try again.'}
          </div>
        )}

        {/* ─── Modal Body ────────────────────────────────────────── */}
        <div className="va-modal-body">
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '16px'
            }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '3px solid #E5E7EB',
                borderTop: '3px solid #F06E18',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#6B7280' }}>Loading agreement...</p>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <h3 style={{ marginTop: '16px', color: '#1F2937' }}>Unable to Load Agreement</h3>
              <p style={{ color: '#6B7280', maxWidth: '400px' }}>{error}</p>
              <button 
                onClick={fetchAgreementData}
                style={{
                  marginTop: '16px',
                  padding: '8px 24px',
                  backgroundColor: '#F06E18',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : !agData ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#6B7280' }}>No agreement data available</p>
            </div>
          ) : isMobile ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#f9fafb',
              padding: '20px',
              textAlign: 'center'
            }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#f06e18" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3" />
              </svg>
              <h3 style={{ marginTop: '16px', color: '#111827', fontSize: '18px' }}>
                Rent Agreement
              </h3>
              <p style={{ color: '#6b7280', maxWidth: '300px', fontSize: '14px' }}>
                Tap <strong>"Open PDF"</strong> above to view your agreement in a new tab.
              </p>
              <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                {tenantName} · Room {roomNumber}
                {isVerified && (
                  <span style={{ 
                    marginLeft: '8px', 
                    color: '#059669',
                    fontWeight: '600'
                  }}>
                    ✓ Verified
                  </span>
                )}
              </p>
            </div>
          ) : (
            <PDFViewer width="100%" height="100%" showToolbar={false}>
              <RentAgreementDocument data={agData} isVerified={isVerified} />
            </PDFViewer>
          )}
        </div>
      </div>
    </div>
  );
};

// Add spinner animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default TenantAgreementModalView;



// // src/components/TenantAgreementModalView.jsx
// import React, { useState, useEffect }  from 'react';
// import { Document, Page, Text, View, Image, StyleSheet, Font, PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
// import { generateAgreementPDF, getAgreementJsonData } from '../apis/services/pgApi';

// // ─────────────────────────────────────────────────────────────────
// // LETTERHEAD ARTWORK
// // ─────────────────────────────────────────────────────────────────
// const LETTERHEAD_FIRST = '/assets/letterhead-first.png';

// // ─────────────────────────────────────────────────────────────────
// // FONT REGISTRATION
// // ─────────────────────────────────────────────────────────────────
// Font.register({
//   family: 'NotoSans',
//   fonts: [
//     { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf', fontWeight: 'normal' },
//     { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Bold.ttf', fontWeight: 'bold' },
//   ],
// });

// // ═════════════════════════════════════════════════════════════════
// // PDF STYLES
// // ═════════════════════════════════════════════════════════════════

// const PAGE_PADDING_TOP = 120;
// const PAGE_PADDING_BOTTOM = 60;
// const PAGE_PADDING_SIDE = 30;

// const HEADER_NAME_TOP = 38;
// const HEADER_NAME_LEFT = 30;
// const HEADER_NAME_WIDTH = 240;
// const HEADER_ROW_LEFT = 340;
// const HEADER_ROW_WIDTH = 210;
// const HEADER_ROW1_TOP = 46;
// const HEADER_ROW2_TOP = 62;
// const HEADER_ROW3_TOP = 79;
// const FOOTER_TEXT_BOTTOM = 40;

// const docStyles = StyleSheet.create({
//   page: {
//     paddingTop: PAGE_PADDING_TOP,
//     paddingBottom: PAGE_PADDING_BOTTOM,
//     paddingHorizontal: PAGE_PADDING_SIDE,
//     fontSize: 9.5,
//     lineHeight: 1.45,
//     fontFamily: 'Helvetica',
//     color: '#1F2937',
//   },
//   background: {
//     position: 'absolute',
//     top: 0,
//     left: 0,
//     width: '100%',
//     height: '100%',
//     zIndex: -1,
//   },
//   title: {
//     fontSize: 13,
//     fontFamily: 'Helvetica-Bold',
//     textAlign: 'center',
//     marginBottom: 12,
//     letterSpacing: 1,
//   },
//   sectionHeading: {
//     fontSize: 10.5,
//     fontFamily: 'Helvetica-Bold',
//     marginTop: 10,
//     marginBottom: 4,
//   },
//   bold: { fontFamily: 'Helvetica-Bold' },
//   keyword: { fontFamily: 'Helvetica-Bold' },
//   rupee: { fontFamily: 'NotoSans' },
//   clause: { marginBottom: 4, textAlign: 'left' },
//   signBlockWrap: { marginTop: 18 },
//   signRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 22 },
//   signCol: { width: '46%' },
//   signLabel: { fontFamily: 'Helvetica-Bold', marginBottom: 4 },
//   signLine: { borderBottomWidth: 1, borderBottomColor: '#374151', marginBottom: 3 },
//   signSub: { fontSize: 8.5, color: '#4B5563' },
//   pgName: {
//     position: 'absolute',
//     top: HEADER_NAME_TOP,
//     left: HEADER_NAME_LEFT,
//     width: HEADER_NAME_WIDTH,
//     fontSize: 24,
//     lineHeight: 1.2,
//     fontFamily: 'Helvetica-Bold',
//     color: '#111827',
//   },
//   pgContactRow: {
//     position: 'absolute',
//     left: HEADER_ROW_LEFT,
//     width: HEADER_ROW_WIDTH,
//     fontSize: 8,
//     lineHeight: 1.25,
//     color: '#374151',
//   },
//   pgFooter: {
//     position: 'absolute',
//     bottom: FOOTER_TEXT_BOTTOM,
//     left: 0,
//     right: 0,
//     textAlign: 'center',
//     fontSize: 11,
//     fontFamily: 'Helvetica-Bold',
//     color: '#111827',
//   },
// });

// // ═════════════════════════════════════════════════════════════════
// // SIMPLIFIED FORM STYLES (for the single-page admission form)
// // ═════════════════════════════════════════════════════════════════
// const simpleStyles = StyleSheet.create({
//   twoColRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
//   colBox: { width: '48%' },
//   infoBox: {
//     borderWidth: 0,
//     borderColor: '#9CA3AF',
//     padding: 8,
//   },
//   infoBoxTitle: {
//     fontFamily: 'Helvetica-Bold',
//     fontSize: 10.5,
//     textDecoration: 'underline',
//     marginBottom: 6,
//   },
//   officeRow: { flexDirection: 'row', marginBottom: 3 },
//   officeLabel: { fontFamily: 'Helvetica-Bold', width: 100 },
// });

// // Renders text containing **bold** markers as mixed bold/normal Text spans
// const RUPEE = '₹';

// // Splits a plain-text run on the rupee sign and renders that glyph in NotoSans,
// // since Helvetica (react-pdf's default base font) has no ₹ glyph and mis-renders it.
// const renderWithRupee = (text, keyPrefix, bold) => {
//   const segments = String(text).split(RUPEE);
//   const nodes = [];
//   segments.forEach((seg, idx) => {
//     if (seg) nodes.push(<Text key={`${keyPrefix}-t${idx}`}>{seg}</Text>);
//     if (idx < segments.length - 1) {
//       nodes.push(
//         <Text key={`${keyPrefix}-r${idx}`} style={[docStyles.rupee, bold && { fontWeight: 'bold' }]}>
//           {RUPEE}
//         </Text>
//       );
//     }
//   });
//   return nodes;
// };

// const parseBoldText = (text) => {
//   const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
//   return parts.map((part, i) =>
//     part.startsWith('**') && part.endsWith('**')
//       ? <Text key={i} style={docStyles.bold}>{renderWithRupee(part.slice(2, -2), `b${i}`, true)}</Text>
//       : <Text key={i}>{renderWithRupee(part, `n${i}`, false)}</Text>
//   );
// };

// const NumberedClause = ({ number, text }) => (
//   <Text style={docStyles.clause}>{number}. {parseBoldText(text)}</Text>
// );

// // Static Terms & Conditions — verbatim from the signed hostel agreement, unchanged
// const STATIC_TERMS = [
//   'Rent is not negotiable post the signing of rent agreement and acceptance of terms & conditions.',
//   'Booking amount (or token money provided) will be **non-refundable on cancellation**.',
//   'Electricity bill of the room and washroom is **excluded from the rent** and should be paid as per the rate **Unit 14.5 Rs. + GST**. The bill to be charged is divisible by number of occupants in the room. In the absence of other occupants, the bill shall be paid wholly by the only occupant.',
//   'Due date of rent payment will be **the 5th of every month**. The delay in payment will attract **a penalty of Rs. 100 per day**.',
//   'PG entry and exit **timings are 7:00 AM and 11 PM respectively.** In the event of delayed arrival or early departure to/from the PG premises, the occupant must inform the Warden/Caretaker/Guard for such arrival/departure in advance.',
//   "Damage to any immovable/movable property of the premises will attract a fine of **up to Rs. 1,000** and it must also compensate for the losses/damages to the PG's property. The figure for losses/damages will be charged **at market value** of the objects/property so damaged and not at any depreciable value.",
//   'Since we provide a budget laundry service at just ₹300/month (not luxury/dry cleaning), our maximum liability for any lost or damaged item is **strictly capped at upto ₹300**. If you prefer premium care, you can opt out and we will deduct ₹300 from your rent.',
//   'Any misbehave with the staff will be not acceptable and strict action will be taken.',
//   'Lock in period in from **1st August to 20th June**.',
//   'The official room **tenure is from 1st August till 20th June**. For students moving in during July, rent will be charged on a daily pro-rata basis.',
//   'Security is **non refundable during the 11 month Tenure** only refund at the end of Tenure.',
//   'Strictly **No Smoking & No Drinking** within the premises.',
//   'Outsiders / visitors are not allowed inside the room.',
//   'Student **cannot vacate the seat before lock in period duration**. If vacate the seat, security will be forfeited.',
// ];

// const Section = ({ children }) => <Text style={docStyles.sectionHeading}>{children}</Text>;

// // ─── DATA TRANSFORMATION FUNCTION ──────────────────────────────
// const transformAgreementData = (apiData) => {
//   if (!apiData) return null;

//   // If data is already in the expected format
//   if (apiData.pg && !Array.isArray(apiData.pg) && apiData.tenant && !Array.isArray(apiData.tenant)) {
//     return apiData;
//   }

//   // Handle array data from API
//   const getFirstItem = (data) => {
//     if (Array.isArray(data) && data.length > 0) {
//       return data[0];
//     }
//     return data || {};
//   };

//   return {
//     agreementDate: apiData.agreementDate || '20-07-2026',
//     agreementDay: apiData.agreementDay || 'Monday',
//     city: apiData.city || 'Delhi',
//     state: apiData.state || 'Delhi',
    
//     pg: getFirstItem(apiData.pg),
//     landlord: getFirstItem(apiData.landlord),
//     tenant: getFirstItem(apiData.tenant),
//     property: getFirstItem(apiData.property),
//     tenancy: getFirstItem(apiData.tenancy),
//     rent: getFirstItem(apiData.rent),
//     deposit: getFirstItem(apiData.deposit),
//     utilities: getFirstItem(apiData.utilities),
//     food: getFirstItem(apiData.food),
//     rules: getFirstItem(apiData.rules),
//     termination: getFirstItem(apiData.termination)
//   };
// };

// // ═════════════════════════════════════════════════════════════════
// // SIMPLIFIED SINGLE-PAGE ADMISSION FORM
// // ═════════════════════════════════════════════════════════════════

// export const RentAgreementDocument = ({ data, isVerified = false } = {}) => {
//   const rawData = data;
//   const d = transformAgreementData(rawData);

//   const pg = d.pg || {};
//   const lo = d.landlord || {};
//   const tn = d.tenant || {};
//   const pr = d.property || {};
//   const tc = d.tenancy || {};

//   return (
//     <Document>
//       <Page size="A4" style={docStyles.page} wrap>
//         <Image
//           src={LETTERHEAD_FIRST}
//           style={{
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             width: 595.28,
//             height: 841.89,
//             zIndex: -1,
//           }}
//           fixed
//         />

//         <Text style={docStyles.pgName} fixed>{pg.name || 'AapkaPG'}</Text>
//         <Text style={[docStyles.pgContactRow, { top: HEADER_ROW1_TOP }]} fixed>{pg.phone || ''}</Text>
//         <Text style={[docStyles.pgContactRow, { top: HEADER_ROW2_TOP }]} fixed>{pg.email || ''}</Text>
//         <Text style={[docStyles.pgContactRow, { top: HEADER_ROW3_TOP }]} fixed>{pg.address || ''}</Text>
//         <Text style={docStyles.pgFooter} fixed>Thank you for choosing {pg.name || 'AapkaPG'}</Text>

//         <Text style={docStyles.title}>RESIDENT ADMISSION FORM & HOSTEL AGREEMENT</Text>

//         {/* ── FOR OFFICE USE ONLY  +  PERSONAL DETAILS (side by side) ── */}
//         <View style={simpleStyles.twoColRow}>
//           <View style={simpleStyles.colBox}>
//             <View style={simpleStyles.infoBox}>
//               <Text style={simpleStyles.infoBoxTitle}>For Office Use Only</Text>
//               <View style={simpleStyles.officeRow}>
//                 <Text style={simpleStyles.officeLabel}>Room No:</Text>
//                 <Text style={docStyles.keyword}>{pr.RoomNmber || ''}</Text>
//               </View>
//               <View style={simpleStyles.officeRow}>
//                 <Text style={simpleStyles.officeLabel}>Joining Date:</Text>
//                 <Text style={docStyles.keyword}>{tc.startDate || ''}</Text>
//               </View>
//             </View>
//           </View>

//           <View style={simpleStyles.colBox}>
//             <View style={simpleStyles.infoBox}>
//               <Text style={simpleStyles.infoBoxTitle}>Personal Details</Text>
//               <Text style={docStyles.clause}>Student Full Name: <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text></Text>
//               <Text style={docStyles.clause}>Contact No (Self): <Text style={docStyles.keyword}>{tn.mobile || ''}</Text></Text>
//               {tn.parent ? (
//                 <Text style={docStyles.clause}>Father's Name: <Text style={docStyles.keyword}>{tn.parent}</Text></Text>
//               ) : null}
//               <Text style={docStyles.clause}>
//                 Permanent Address: <Text style={docStyles.keyword}>{tn.address || ''}</Text>
//               </Text>
//               <Text style={docStyles.clause}>
//                 {tn.pin ? <Text>PIN: <Text style={docStyles.keyword}>{tn.pin}</Text></Text> : null}
//               </Text>
//               {tn.email ? (
//                 <Text style={docStyles.clause}>Email ID: <Text style={docStyles.keyword}>{tn.email}</Text></Text>
//               ) : null}
//             </View>
//           </View>
//         </View>

//         {/* ── TERMS & CONDITIONS (static, unchanged) ──────────── */}
//         <Section>Terms & Conditions</Section>
//         {STATIC_TERMS.map((t, i) => (
//           <NumberedClause key={i} number={i + 1} text={t} />
//         ))}

//         {/* ── SIGNATURES (unchanged from the full agreement) ──── */}
//         <View style={docStyles.signBlockWrap}>
//           <View style={docStyles.signRow}>
//             <View style={docStyles.signCol}>
//               <Text style={docStyles.signLabel}>Landlord</Text>
//               <Text style={docStyles.signSub}>Signature: {lo.name || ''}</Text>
//               <View style={docStyles.signLine} />
//               {/* <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{lo.title || 'Mr.'} {lo.name || ''}</Text></Text> */}
//               <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
//             </View>
//             <View style={docStyles.signCol}>
//               <Text style={docStyles.signLabel}>Tenant</Text>
//               <Text style={docStyles.signSub}>Signature:
//                 {isVerified ? (
//                   <Text> {tn.name || ''}</Text>
//                 ) : (
//                   <Text> </Text>
//                 )}
//               </Text>
//               <View style={docStyles.signLine} />
//               {/* <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text></Text> */}
//               <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
//             </View>
//           </View>
//         </View>
//       </Page>
//     </Document>
//   );
// };

// // ═════════════════════════════════════════════════════════════════
// // TENANT AGREEMENT MODAL VIEW - Main Component
// // ═════════════════════════════════════════════════════════════════

// const TenantAgreementModalView = ({ 
//   isOpen, 
//   onClose, 
//   pgId, 
//   tenantId,
//   agreementStatus = 'DRAFT',
//   statusText = 'Draft',
//   statusColor = '#F59E0B',
//   showSaveToCloud = false,
//   onUploadSuccess
// }) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [isMobile, setIsMobile] = useState(false);
//   const [isUploading, setIsUploading] = useState(false);
//   const [uploadStatus, setUploadStatus] = useState(null);
//   const [uploadError, setUploadError] = useState(null);

//   // Check if mobile
//   useEffect(() => {
//     const userAgent = navigator.userAgent || navigator.vendor || window.opera;
//     const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
//     setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
//   }, []);


//    const handleOpenPDF = async () => {
//       const { pdf } = await import('@react-pdf/renderer');
//       const blob = await pdf(<RentAgreementDocument data={agData} isVerified={isVerified} />).toBlob();
//       const url = URL.createObjectURL(blob);
//       window.open(url, '_blank');
//       setTimeout(() => URL.revokeObjectURL(url), 60000);
//     };
    
//   // Fetch agreement data when modal opens
//   useEffect(() => {
//     if (isOpen && pgId && tenantId) {
//       fetchAgreementData();
//     }
//   }, [isOpen, pgId, tenantId]);

//   const fetchAgreementData = async () => {
//     if (!pgId || !tenantId) {
//       setError('PG ID and Tenant ID are required');
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const response = await getAgreementJsonData(pgId, tenantId);
//       console.log('Agreement API Response:', response);

//       if (response.success && response.data) {
//         let parsedData = response.data;
        
//         // Parse agreementjson if it's a string
//         if (typeof response.data.agreementjson === 'string') {
//           try {
//             const jsonData = JSON.parse(response.data.agreementjson);
//             if (Array.isArray(jsonData) && jsonData.length > 0) {
//               parsedData = {
//                 ...response.data,
//                 agreementjson: jsonData[0]
//               };
//             } else {
//               parsedData = {
//                 ...response.data,
//                 agreementjson: jsonData
//               };
//             }
//           } catch (parseError) {
//             console.error('Error parsing agreement JSON:', parseError);
//             parsedData = response.data;
//           }
//         } else if (Array.isArray(response.data.agreementjson)) {
//           parsedData = {
//             ...response.data,
//             agreementjson: response.data.agreementjson[0] || response.data.agreementjson
//           };
//         }

//         setAgreementData(parsedData);
//       } else {
//         setError(response.message || 'Failed to fetch agreement data');
//       }
//     } catch (err) {
//       console.error('Error fetching agreement:', err);
//       setError(err.message || 'An error occurred while fetching agreement');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── Upload to Cloud ──────────────────────────────────────────────
//   const handleGenerateAndUpload = async () => {
//     const agreementId = agreementData?.AgreementID;
    
//     if (!agreementId) {
//       alert('Agreement ID is required to save the document.');
//       return;
//     }

//     try {
//       setIsUploading(true);
//       setUploadStatus('generating');
//       setUploadError(null);

//       // Generate PDF as blob
//       const pdfBlob = await pdf(
//         <RentAgreementDocument data={agreementData?.agreementjson} isVerified={agreementStatus === 'TENANT_ACCEPTED'} />
//       ).toBlob();

//       // Create File object
//       const file = new File(
//         [pdfBlob],
//         `agreement_${agreementId}_${Date.now()}.pdf`,
//         { type: 'application/pdf' }
//       );

//       setUploadStatus('uploading');

//       // Call API to upload
//       const result = await generateAgreementPDF(
//         agreementId,
//         file
//       );

//       if (result.success) {
//         setUploadStatus('success');
//         console.log('✅ PDF uploaded successfully:', result.data);
        
//         if (onUploadSuccess) {
//           onUploadSuccess(result.data);
//         }
//       } else {
//         setUploadStatus('error');
//         setUploadError(result.message || 'Failed to upload PDF');
//       }
//     } catch (error) {
//       console.error('Error:', error);
//       setUploadStatus('error');
//       setUploadError(error.message || 'An error occurred while uploading');
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   // ─── Close handler ──────────────────────────────────────────────
//   const handleClose = () => {
//     setError(null);
//     setUploadStatus(null);
//     setUploadError(null);
//     onClose();
//   };

//   // ─── Render ──────────────────────────────────────────────────────
//   if (!isOpen) return null;

//   const agData = agreementData?.agreementjson || null;
//   const tenantName = agData?.tenant?.[0]?.name || agData?.tenant?.name || 'Tenant';
//   const roomNumber = agData?.property?.[0]?.RoomNmber || agData?.property?.RoomNmber || 'TBD';
//   const agreementId = agreementData?.AgreementID;
//   const isVerified = agreementStatus === 'TENANT_ACCEPTED';

//   return (
//     <div className="va-overlay" onClick={handleClose}>
//       <div className="va-modal" onClick={e => e.stopPropagation()}>
//         {/* ─── Modal Header ─────────────────────────────────────── */}
//         <div className="va-modal-head">
//           <div>
//             <h3 className="va-modal-title">Rent Agreement</h3>
//             <p className="va-modal-sub">
//               {tenantName} · Room {roomNumber}
//               {agreementStatus && (
//                 <span style={{ 
//                   marginLeft: '12px',
//                   fontSize: '12px',
//                   fontWeight: '600',
//                   color: statusColor,
//                   padding: '2px 10px',
//                   borderRadius: '12px',
//                   backgroundColor: `${statusColor}15`,
//                   border: `1px solid ${statusColor}30`,
//                 }}>
//                   {statusText}
//                 </span>
//               )}
//             </p>
//           </div>

//           <div className="va-modal-actions">
//             {/* Save to Cloud Button */}
//             {agreementId && showSaveToCloud && (
//               <button 
//                 className="va-upload-btn" 
//                 onClick={handleGenerateAndUpload}
//                 disabled={isUploading}
//                 style={{
//                   padding: '8px 16px',
//                   backgroundColor: isUploading ? '#9CA3AF' : '#059669',
//                   color: 'white',
//                   border: 'none',
//                   borderRadius: '6px',
//                   cursor: isUploading ? 'not-allowed' : 'pointer',
//                   marginRight: '8px',
//                   display: 'flex',
//                   alignItems: 'center',
//                   gap: '8px',
//                   fontSize: '13px',
//                   fontWeight: '500',
//                 }}
//               >
//                 {isUploading ? (
//                   <>
//                     <span className="spinner" style={{
//                       display: 'inline-block',
//                       width: '14px',
//                       height: '14px',
//                       border: '2px solid white',
//                       borderTop: '2px solid transparent',
//                       borderRadius: '50%',
//                       animation: 'spin 1s linear infinite'
//                     }}></span>
//                     {uploadStatus === 'generating' ? 'Generating...' : 'Uploading...'}
//                   </>
//                 ) : (
//                   <>
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                       <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
//                     </svg>
//                     Save to Cloud
//                   </>
//                 )}
//               </button>
//             )}

//             {isMobile && ( 
//               <button className="va-download-btn" onClick={handleOpenPDF} style={{ marginRight: '8px' }}>
//                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
//                   <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
//                 </svg>
//                 Open PDF
//               </button>
//             )}
            
//             {agData && (
//               <PDFDownloadLink
//                 document={<RentAgreementDocument data={agData} isVerified={isVerified} />}
//                 fileName={`Agreement_${agreementId || 'Rent'}.pdf`}
//                 className="va-download-btn"
//               >
//                 {({ loading }) => (
//                   <>
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
//                       <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
//                     </svg>
//                     {loading ? 'Preparing…' : 'Download PDF'}
//                   </>
//                 )}
//               </PDFDownloadLink>
//             )}

//             <button className="va-modal-close" onClick={handleClose} aria-label="Close">
//               <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//                 <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//               </svg>
//             </button>
//           </div>
//         </div>

//         {/* ─── Upload Status Messages ──────────────────────────── */}
//         {uploadStatus === 'success' && (
//           <div style={{
//             padding: '10px 20px',
//             backgroundColor: '#D1FAE5',
//             color: '#065F46',
//             borderBottom: '1px solid #A7F3D0',
//             display: 'flex',
//             alignItems: 'center',
//             gap: '8px'
//           }}>
//             <span style={{ fontSize: '18px' }}>✅</span>
//             PDF uploaded successfully to cloud storage
//           </div>
//         )}
//         {uploadStatus === 'error' && (
//           <div style={{
//             padding: '10px 20px',
//             backgroundColor: '#FEE2E2',
//             color: '#991B1B',
//             borderBottom: '1px solid #FCA5A5',
//             display: 'flex',
//             alignItems: 'center',
//             gap: '8px'
//           }}>
//             <span style={{ fontSize: '18px' }}>❌</span>
//             {uploadError || 'Failed to upload PDF. Please try again.'}
//           </div>
//         )}

//         {/* ─── Modal Body ────────────────────────────────────────── */}
//         <div className="va-modal-body">
//           {loading ? (
//             <div style={{
//               display: 'flex',
//               flexDirection: 'column',
//               alignItems: 'center',
//               justifyContent: 'center',
//               height: '100%',
//               gap: '16px'
//             }}>
//               <div className="spinner" style={{
//                 width: '40px',
//                 height: '40px',
//                 border: '3px solid #E5E7EB',
//                 borderTop: '3px solid #F06E18',
//                 borderRadius: '50%',
//                 animation: 'spin 1s linear infinite'
//               }}></div>
//               <p style={{ color: '#6B7280' }}>Loading agreement...</p>
//             </div>
//           ) : error ? (
//             <div style={{
//               display: 'flex',
//               flexDirection: 'column',
//               alignItems: 'center',
//               justifyContent: 'center',
//               height: '100%',
//               padding: '40px',
//               textAlign: 'center'
//             }}>
//               <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5">
//                 <circle cx="12" cy="12" r="10" />
//                 <line x1="12" y1="8" x2="12" y2="12" />
//                 <line x1="12" y1="16" x2="12.01" y2="16" />
//               </svg>
//               <h3 style={{ marginTop: '16px', color: '#1F2937' }}>Unable to Load Agreement</h3>
//               <p style={{ color: '#6B7280', maxWidth: '400px' }}>{error}</p>
//               <button 
//                 onClick={fetchAgreementData}
//                 style={{
//                   marginTop: '16px',
//                   padding: '8px 24px',
//                   backgroundColor: '#F06E18',
//                   color: 'white',
//                   border: 'none',
//                   borderRadius: '6px',
//                   cursor: 'pointer'
//                 }}
//               >
//                 Retry
//               </button>
//             </div>
//           ) : !agData ? (
//             <div style={{
//               display: 'flex',
//               flexDirection: 'column',
//               alignItems: 'center',
//               justifyContent: 'center',
//               height: '100%',
//               padding: '40px',
//               textAlign: 'center'
//             }}>
//               <p style={{ color: '#6B7280' }}>No agreement data available</p>
//             </div>
//           ) : isMobile ? (
//             <div style={{
//               display: 'flex',
//               flexDirection: 'column',
//               alignItems: 'center',
//               justifyContent: 'center',
//               height: '100%',
//               backgroundColor: '#f9fafb',
//               padding: '20px',
//               textAlign: 'center'
//             }}>
//               <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#f06e18" strokeWidth="1.5">
//                 <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
//                 <path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3" />
//               </svg>
//               <h3 style={{ marginTop: '16px', color: '#111827', fontSize: '18px' }}>
//                 Rent Agreement
//               </h3>
//               <p style={{ color: '#6b7280', maxWidth: '300px', fontSize: '14px' }}>
//                 Tap <strong>"Open PDF"</strong> above to view your agreement in a new tab.
//               </p>
//               <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
//                 {tenantName} · Room {roomNumber}
//                 {isVerified && (
//                   <span style={{ 
//                     marginLeft: '8px', 
//                     color: '#059669',
//                     fontWeight: '600'
//                   }}>
//                     ✓ Verified
//                   </span>
//                 )}
//               </p>
//             </div>
//           ) : (
//             <PDFViewer width="100%" height="100%" showToolbar={false}>
//               <RentAgreementDocument data={agData} isVerified={isVerified} />
//             </PDFViewer>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// // Add spinner animation CSS
// const style = document.createElement('style');
// style.textContent = `
//   @keyframes spin {
//     0% { transform: rotate(0deg); }
//     100% { transform: rotate(360deg); }
//   }
// `;
// document.head.appendChild(style);

// export default TenantAgreementModalView;